/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Lock, Check, X, Eye, EyeOff } from "lucide-react";

const SetPasswordForm = ({ onSuccessfulLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [requirements, setRequirements] = useState({
    minLength: 12,
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
    requireSymbol: true
  });

  // Get context from navigation state
  const { firstName, isNewPassword, message } = location.state || {};

  useEffect(() => {
    // Fetch password requirements from server
    const fetchRequirements = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/password-requirements`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setRequirements(data);
        }
      } catch (err) {
        console.warn('Failed to fetch password requirements:', err);
      }
    };
    fetchRequirements();
  }, []);

  // Check which requirements are met
  const requirementStatus = useMemo(() => {
    const password = formData.newPassword;
    return {
      length: password.length >= requirements.minLength,
      uppercase: !requirements.requireUppercase || /[A-Z]/.test(password),
      lowercase: !requirements.requireLowercase || /[a-z]/.test(password),
      number: !requirements.requireNumber || /[0-9]/.test(password),
      symbol: !requirements.requireSymbol || /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password),
      match: password.length > 0 && password === formData.confirmPassword
    };
  }, [formData.newPassword, formData.confirmPassword, requirements]);

  // Calculate password strength (0-100)
  const passwordStrength = useMemo(() => {
    const checks = [
      requirementStatus.length,
      requirementStatus.uppercase,
      requirementStatus.lowercase,
      requirementStatus.number,
      requirementStatus.symbol
    ];
    const passed = checks.filter(Boolean).length;
    return (passed / checks.length) * 100;
  }, [requirementStatus]);

  const getStrengthColor = () => {
    if (passwordStrength < 40) return 'bg-red-500';
    if (passwordStrength < 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStrengthLabel = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 80) return 'Moderate';
    return 'Strong';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    setError('');
  };

  const allRequirementsMet = Object.values(requirementStatus).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRequirementsMet) {
      setError('Please ensure all password requirements are met.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to set password');
      }

      // Password set successfully - redirect to staff login
      navigate('/staff-login', {
        state: {
          message: 'Password set successfully. Please log in with your new password.'
        }
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const RequirementItem = ({ met, label }) => (
    <div className={`flex items-center gap-2 text-sm ${met ? 'text-green-600' : 'text-gray-500'}`}>
      {met ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <X className="h-4 w-4 text-gray-400" />
      )}
      <span>{label}</span>
    </div>
  );

  return (
    <Card className="w-full max-w-md mx-auto m-2 sm:m-4">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-xl sm:text-2xl">
            {isNewPassword ? 'Set Your Password' : 'Change Password'}
          </CardTitle>
        </div>
        <CardDescription className="text-sm sm:text-base">
          {firstName && <span className="block mb-1">Welcome, {firstName}!</span>}
          {message || 'Create a secure password for your staff account.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <div className="relative">
              <Input
                id="newPassword"
                name="newPassword"
                type={showPassword ? 'text' : 'password'}
                required
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleInputChange}
                disabled={isLoading}
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {/* Password strength bar */}
            {formData.newPassword && (
              <div className="space-y-1">
                <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${getStrengthColor()}`}
                    style={{ width: `${passwordStrength}%` }}
                  />
                </div>
                <p className={`text-xs ${getStrengthColor().replace('bg-', 'text-')}`}>
                  Password strength: {getStrengthLabel()}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                disabled={isLoading}
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Password requirements checklist */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
            <RequirementItem
              met={requirementStatus.length}
              label={`At least ${requirements.minLength} characters`}
            />
            {requirements.requireUppercase && (
              <RequirementItem met={requirementStatus.uppercase} label="One uppercase letter (A-Z)" />
            )}
            {requirements.requireLowercase && (
              <RequirementItem met={requirementStatus.lowercase} label="One lowercase letter (a-z)" />
            )}
            {requirements.requireNumber && (
              <RequirementItem met={requirementStatus.number} label="One number (0-9)" />
            )}
            {requirements.requireSymbol && (
              <RequirementItem met={requirementStatus.symbol} label="One special character (!@#$%...)" />
            )}
            <RequirementItem met={requirementStatus.match} label="Passwords match" />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || !allRequirementsMet}
          >
            {isLoading ? 'Setting Password...' : 'Set Password'}
          </Button>

          <p className="text-xs text-center text-gray-500 mt-4">
            After setting your password, you'll be redirected to log in.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default SetPasswordForm;
